import { useCallback, useEffect, useState } from "react";
import { t } from "../locale";
import { slottingErrorText } from "../lib/slotting-error";
import { runtimeConfig } from "../../lib/runtimeConfig";
import { useEventTimezone } from "../theme/WBThemeRuntimeProvider";
import {
  fetchAppointmentPage,
  cancelAppointment,
  type AppointmentPageData,
} from "../../lib/slottingApi";

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  CONFIRMED: { bg: "rgba(34,197,94,0.15)", color: "#16a34a" },
  TENTATIVE: { bg: "rgba(234,179,8,0.15)", color: "#a16207" },
  CANCELLED: { bg: "rgba(239,68,68,0.15)", color: "#dc2626" },
  CHECKED_IN: { bg: "rgba(59,130,246,0.15)", color: "#2563eb" },
};

export function SlottingAppointment({ appointmentId: propId, theme }: { appointmentId?: string; theme?: 'light' | 'dark' }) {
  const params = new URLSearchParams(window.location.search);
  const token = propId || params.get("appointmentId") || "";

  const [data, setData] = useState<AppointmentPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState("");
  const eventTz = useEventTimezone();

  useEffect(() => {
    let active = true;
    if (!token) { setLoading(false); setError(t("slottingAppointmentNotFound")); return; }
    fetchAppointmentPage(token)
      .then((d) => { if (!active) return; setData(d); })
      .catch((err) => { if (!active) return; setError(slottingErrorText(err, "slottingAppointmentNotFound")); })
      .finally(() => { if (!active) return; setLoading(false); });
    return () => { active = false; };
  }, [token]);

  const handleCancel = useCallback(async () => {
    setCancelLoading(true);
    setCancelError("");
    try {
      await cancelAppointment(token);
      setCancelled(true);
    } catch (err) {
      setCancelError(slottingErrorText(err, "slottingCancellationFailed"));
    } finally {
      setCancelLoading(false);
    }
  }, [token]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
        <div style={{ width: 20, height: 20, border: "2px solid #ddd", borderTopColor: "#121212", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
      </div>
    );
  }

  if (error || !data) {
    return <div style={{ textAlign: "center", padding: "48px 0", color: "#64748b" }}>{error || t("slottingAppointmentNotFound")}</div>;
  }

  // Rescheduled
  if (data.rescheduled && data.newAppointmentId && data.newAppointmentId !== token) {
    return (
      <div style={{ textAlign: "center", padding: "48px 16px" }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>{t("slottingRescheduled")}</h2>
        <button
          onClick={() => {
            const url = new URL(window.location.href);
            url.searchParams.set("appointmentId", data.newAppointmentId!);
            window.history.replaceState({}, "", url.toString());
            window.location.reload();
          }}
          style={{ marginTop: 16, padding: "10px 24px", borderRadius: 8, background: "#2563eb", color: "#fff", border: "none", cursor: "pointer", fontWeight: 500 }}
        >
          {t("slottingViewNewAppointment")} &rarr;
        </button>
      </div>
    );
  }

  const { appointment, offering, lane, treatment } = data;
  const status = appointment.status;
  const startDate = new Date(appointment.startDt);
  const endDate = new Date(appointment.endDt);
  // Offering zone, else event zone — the rule the picker (4d), the waitlist
  // page and the DataInput summary follow. Rendering in the browser zone
  // showed a visitor abroad a different hour than the slot they had just
  // picked (fix round 1, l9 #2).
  const offeringTz = offering?.timezone || eventTz;
  const statusStyle = STATUS_COLORS[status] || { bg: "#f1f5f9", color: "#475569" };

  const isActionable = status === "CONFIRMED" || status === "TENTATIVE";
  let deadlinePassed = false;
  if (isActionable && offering?.cancellationDeadlineHours != null) {
    const deadline = new Date(startDate.getTime() - offering.cancellationDeadlineHours * 3600000);
    deadlinePassed = new Date() > deadline;
  }

  const icsUrl = `${runtimeConfig.apiBase}/slotting/appointment/${token}/ics`;

  return (
    <div className={theme === 'dark' ? 'dark' : ''}>
      <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 24 }}>{t("slottingYourAppointment")}</h2>

      <div style={{ border: "1px solid #e2e8f0", borderRadius: 12, padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Status badge */}
        <span style={{ display: "inline-block", padding: "4px 12px", borderRadius: 999, fontSize: 12, fontWeight: 500, background: statusStyle.bg, color: statusStyle.color, alignSelf: "flex-start" }}>
          {status === "CONFIRMED" ? t("slottingConfirmed") :
           status === "TENTATIVE" ? t("slottingTentative") :
           status === "CANCELLED" ? t("slottingCancelled") :
           status === "CHECKED_IN" ? t("slottingCheckedIn") :
           status.replace("_", " ")}
        </span>

        {treatment && (
          <div>
            <div style={{ fontSize: 13, color: "#64748b" }}>{t("slottingService")}</div>
            <div style={{ fontWeight: 500 }}>{treatment.name}</div>
          </div>
        )}

        {lane && (
          <div>
            <div style={{ fontSize: 13, color: "#64748b" }}>{t("slottingLocation")}</div>
            <div>{lane.name}</div>
          </div>
        )}

        <div>
          <div style={{ fontSize: 13, color: "#64748b" }}>{t("slottingDateTime")}</div>
          <div>{startDate.toLocaleDateString(undefined, { timeZone: offeringTz, weekday: "long", year: "numeric", month: "long", day: "numeric" })}</div>
          <div>
            {startDate.toLocaleTimeString(undefined, { timeZone: offeringTz, hour: "2-digit", minute: "2-digit" })}
            {" \u2013 "}
            {/* Name the zone once: nothing else on the slotting surfaces says which clock "09:00" is. */}
            {endDate.toLocaleTimeString(undefined, { timeZone: offeringTz, hour: "2-digit", minute: "2-digit", timeZoneName: "short" })}
          </div>
        </div>

        {offering && (
          <div>
            <div style={{ fontSize: 13, color: "#64748b" }}>{t("slottingOffering")}</div>
            <div>{offering.name}</div>
          </div>
        )}

        {/* ICS download */}
        {status !== "CANCELLED" && (
          <a
            href={icsUrl}
            download="appointment.ics"
            style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 14, textDecoration: "none", color: "inherit", cursor: "pointer", alignSelf: "flex-start" }}
          >
            &#128197; {t("slottingDownloadCalendar")}
          </a>
        )}
      </div>

      {/* Cancelled */}
      {(status === "CANCELLED" || cancelled) && (
        <div style={{ marginTop: 24, padding: 16, borderRadius: 8, background: "rgba(239,68,68,0.1)", color: "#dc2626", fontSize: 14 }}>
          {cancelled ? t("slottingCancelledSuccess") : t("slottingCancelledMessage")}
        </div>
      )}

      {/* Read-only states */}
      {status === "CHECKED_IN" && (
        <div style={{ marginTop: 24, padding: 16, borderRadius: 8, background: "#f8fafc", color: "#64748b", fontSize: 14 }}>
          {t("slottingStatusMessage", t("slottingInProgress"))}
        </div>
      )}

      {/* Cancel actions */}
      {isActionable && !deadlinePassed && !cancelled && (
        <div style={{ marginTop: 24 }}>
          {cancelError && (
            <div style={{ padding: 12, borderRadius: 8, background: "rgba(239,68,68,0.1)", color: "#dc2626", fontSize: 14, marginBottom: 12 }}>
              {cancelError}
            </div>
          )}
          {!showCancel ? (
            <button
              onClick={() => setShowCancel(true)}
              style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid #fca5a5", color: "#dc2626", background: "transparent", fontSize: 14, fontWeight: 500, cursor: "pointer" }}
            >
              {t("slottingCancelButton")}
            </button>
          ) : (
            <div style={{ padding: 16, borderRadius: 8, border: "1px solid #fecaca", display: "flex", flexDirection: "column", gap: 12 }}>
              <p style={{ fontSize: 14 }}>{t("slottingCancelConfirm")}</p>
              <div style={{ display: "flex", gap: 12 }}>
                <button
                  onClick={handleCancel}
                  disabled={cancelLoading}
                  style={{ padding: "8px 20px", borderRadius: 8, background: "#dc2626", color: "#fff", border: "none", fontSize: 14, fontWeight: 500, cursor: "pointer", opacity: cancelLoading ? 0.5 : 1 }}
                >
                  {cancelLoading ? t("slottingCancelling") : t("slottingYesCancel")}
                </button>
                <button
                  onClick={() => setShowCancel(false)}
                  style={{ padding: "8px 20px", borderRadius: 8, border: "1px solid #e2e8f0", background: "transparent", fontSize: 14, cursor: "pointer" }}
                >
                  {t("slottingKeepAppointment")}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {isActionable && deadlinePassed && !cancelled && (
        <div style={{ marginTop: 24, padding: 16, borderRadius: 8, background: "rgba(234,179,8,0.1)", color: "#a16207", fontSize: 14 }}>
          {t("slottingDeadlinePassed")}
        </div>
      )}
    </div>
  );
}
